import * as React from 'react';
import { Flex } from '../../Flex';
import { Text } from '@/hen-ui/atomics/Text';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Button } from '@/hen-ui/atomics/Button';
import { Badge } from '@/hen-ui/atomics/Badge';
import { cn } from '@/hen-ui/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/hen-ui/composites/Table';
import type {
  ComparisonColumn,
  ComparisonRowDef,
  ComparisonTableApi,
  ComparisonCtaLocation,
} from '../types';
import { isEmptyColumn } from '../utils/helpers';
import { useTableScrollApi, LABEL_COL_PX } from '../hooks/useTableScrollApi';
import { ComparisonItemImage } from './ComparisonItemImage';
import { EmptySlotHeader } from './EmptySlotHeader';
import { CellContent } from './CellContent';

interface ComparisonTableDesktopProps {
  columns: ComparisonColumn[];
  rowDefs: ComparisonRowDef[];
  visibleItems?: number;
  setApi?: (api: ComparisonTableApi | null) => void;
  inView?: boolean;
  onPageChange?: (page: number) => void;
  onProductClicked?: (id: string) => void;
  onCtaClick?: (
    id: string,
    cta: { label: string; href: string },
    location: ComparisonCtaLocation,
  ) => void;
}

export const ComparisonTableDesktop: React.FC<ComparisonTableDesktopProps> = ({
  columns,
  rowDefs,
  visibleItems = 3,
  setApi,
  inView: _inView = false,
  onPageChange,
  onProductClicked,
  onCtaClick,
}) => {
  const isCarousel = !!setApi;
  const displayColumns = isCarousel ? columns : columns.slice(0, visibleItems);

  // Divider row rendered between body rows and before the CTA row.
  const dividerRow = (
    <TableRow className='border-none relative z-30'>
      {/* Sticky left: label col — ml-4 left inset */}
      <TableCell className='p-0 sticky left-0 z-20 bg-white'>
        <div className='ml-4 divider-line' />
      </TableCell>
      {/* Sticky: first data col */}
      <TableCell
        className='p-0 sticky z-10 bg-white'
        style={{ left: LABEL_COL_PX }}
      >
        <div className='divider-line' />
      </TableCell>
      {/* Middle cols — scrollable, full-bleed */}
      {displayColumns.length > 2 && (
        <TableCell colSpan={displayColumns.length - 2} className='p-0'>
          <div className='divider-line' />
        </TableCell>
      )}
      {/* Sticky right: last col — always visible, mr-6 right inset */}
      {displayColumns.length > 1 && (
        <TableCell className='p-0 sticky right-0 z-10 bg-white'>
          <div className='mr-6 divider-line' />
        </TableCell>
      )}
    </TableRow>
  );

  const {
    scrollContainerRef,
    headerScrollContainerRef,
    imageScrollContainerRef,
    colStyle,
    tableMinWidth,
    currentPage,
  } = useTableScrollApi({
    columnCount: displayColumns.length,
    visibleItems,
    setApi,
  });

  React.useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage, onPageChange]);

  const tableStyle = tableMinWidth ? { minWidth: tableMinWidth } : undefined;

  const colgroup = (
    <colgroup>
      <col style={{ width: LABEL_COL_PX, minWidth: LABEL_COL_PX }} />
      {displayColumns.map((column) => (
        <col key={column.id} style={colStyle} />
      ))}
    </colgroup>
  );

  return (
    <Flex
      direction='column'
      gap='none'
      className='hidden md:flex w-full relative bg-white rounded-xl'
    >
      {/*
       * Image row — NOT sticky, scrolls away naturally with the page.
       * Synced horizontally with the main scroll container via
       * imageScrollContainerRef in useTableScrollApi.
       */}
      <Flex
        ref={imageScrollContainerRef}
        gap='none'
        className='overflow-x-scroll overscroll-x-none scrollbar-none'
      >
        <Table className='table-fixed w-full' noWrapper style={tableStyle}>
          {colgroup}
          <TableHeader>
            <TableRow className='border-none'>
              <TableHead className='sticky left-0 z-20 bg-white rounded-tl-xl' />
              {displayColumns.map((column, index) => (
                <TableHead
                  key={`${column.id}-image`}
                  className={cn(
                    'pt-6 pb-0 align-top h-auto',
                    index === 0 && 'sticky z-10 bg-white pl-6',
                    index === displayColumns.length - 1 && 'pr-6',
                  )}
                  style={index === 0 ? { left: LABEL_COL_PX } : undefined}
                >
                  <Flex direction='column' gap='xs'>
                    {!isEmptyColumn(column) && index === 0 && column.badge ? (
                      <Badge
                        variant={column.badge.variant}
                        size={column.badge.size ?? 'sm'}
                      >
                        {column.badge.text}
                      </Badge>
                    ) : (
                      <Badge variant='primary' size='sm' className='invisible'>
                        &nbsp;
                      </Badge>
                    )}
                    {isEmptyColumn(column) ? (
                      <EmptySlotHeader slot={column} />
                    ) : (
                      <ComparisonItemImage item={column} />
                    )}
                  </Flex>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        </Table>
      </Flex>

      {/*
       * Sticky header — pins to top when scrolled. Contains only
       * SectionHeader (title + CTA), no images to avoid load flashes.
       */}
      <Flex
        ref={headerScrollContainerRef}
        gap='none'
        className='sticky top-0 z-40 bg-white overflow-x-scroll overscroll-x-none scrollbar-none pb-px'
      >
        <Table className='table-fixed w-full' noWrapper style={tableStyle}>
          {colgroup}
          <TableHeader>
            {/* Row 1: title + subtext */}
            {(() => {
              const hasCta = displayColumns.some(
                (col) => !isEmptyColumn(col) && col.header.ctaTop,
              );
              return (
                <TableRow className='border-none'>
                  <TableHead
                    className={cn(
                      'sticky left-0 z-20 bg-white',
                      hasCta ? '' : 'pb-6 row-divider',
                    )}
                  />
                  {displayColumns.map((column, index) => (
                    <TableHead
                      key={`${column.id}-header-title`}
                      className={cn(
                        'pt-2 align-top overflow-hidden',
                        hasCta ? 'pb-0' : 'pb-6 row-divider',
                        index === 0 && 'sticky z-10 bg-white pl-6',
                        index === displayColumns.length - 1 && 'pr-6',
                      )}
                      style={index === 0 ? { left: LABEL_COL_PX } : undefined}
                    >
                      {isEmptyColumn(column) ? (
                        <Heading variant='h5' className='font-normal'>
                          {column.label}
                        </Heading>
                      ) : (
                        <Flex direction='column' gap='xxs'>
                          <Heading variant='h5'>{column.header.title}</Heading>
                          {column.header.subtext && (
                            <Text variant='b5' color='subtext'>
                              {column.header.subtext}
                            </Text>
                          )}
                        </Flex>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              );
            })()}
            {/* Row 2: CTAs — own row so they share the same baseline */}
            {displayColumns.some(
              (col) => !isEmptyColumn(col) && col.header.ctaTop,
            ) && (
              <TableRow className='border-none'>
                <TableHead className='sticky left-0 z-20 bg-white pb-6 row-divider' />
                {displayColumns.map((column, index) => (
                  <TableHead
                    key={`${column.id}-header-cta`}
                    className={cn(
                      'pt-2 pb-6 align-top overflow-hidden row-divider',
                      index === 0 && 'sticky z-10 bg-white pl-6',
                      index === displayColumns.length - 1 && 'pr-6',
                    )}
                    style={index === 0 ? { left: LABEL_COL_PX } : undefined}
                  >
                    {!isEmptyColumn(column) && column.header.ctaTop && (
                      <Button
                        href={column.header.ctaTop.href}
                        variant='primaryFill'
                        size='sm'
                        onClick={() => {
                          column.header.ctaTop!.onClick?.();
                          onCtaClick?.(column.id, column.header.ctaTop!, 'top');
                          onProductClicked?.(column.id);
                        }}
                      >
                        {column.header.ctaTop.label}
                      </Button>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            )}
          </TableHeader>
        </Table>
      </Flex>

      <Flex
        ref={scrollContainerRef}
        direction='column'
        gap='none'
        className={cn(
          'scrollbar-none',
          isCarousel ? 'overflow-x-auto overscroll-x-none' : undefined,
        )}
      >
        <Table
          aria-label='Comparison'
          className='table-fixed w-full'
          noWrapper
          style={tableStyle}
        >
          {colgroup}
          <caption className='sr-only'>Comparison table</caption>

          <TableBody>
            {rowDefs.map((row, rowIndex) => (
              <React.Fragment key={row.key}>
                {rowIndex > 0 && dividerRow}
                <TableRow className='border-none'>
                  <TableHead
                    scope='row'
                    className={cn(
                      'sticky left-0 z-20 bg-white align-middle',
                      rowIndex === 0 ? 'pt-md pb-6' : 'py-6',
                    )}
                  >
                    <Text variant='l1' color='subtext'>
                      {row.label}
                    </Text>
                  </TableHead>
                  {displayColumns.map((column, index) => (
                    <TableCell
                      key={column.id}
                      className={cn(
                        'align-middle overflow-hidden',
                        rowIndex === 0 ? 'pt-md pb-6' : 'py-6',
                        index === 0 && 'sticky z-10 bg-white pl-6',
                        index === displayColumns.length - 1 && 'pr-6',
                      )}
                      style={index === 0 ? { left: LABEL_COL_PX } : undefined}
                    >
                      {!isEmptyColumn(column) && (
                        <CellContent row={row} value={column.fields[row.key]} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}

            {/* Inset divider before CTA row */}
            {dividerRow}

            {/* CTA Bottom Row */}
            <TableRow className='border-none hover:bg-transparent'>
              <TableCell className='py-6 sticky left-0 z-20 bg-white rounded-bl-xl' />
              {displayColumns.map((column, index) => (
                <TableCell
                  key={`${column.id}-cta`}
                  className={cn(
                    'py-6 overflow-hidden',
                    index === 0 && 'sticky z-10 bg-white pl-6',
                    index === displayColumns.length - 1 && 'pr-6',
                  )}
                  style={index === 0 ? { left: LABEL_COL_PX } : undefined}
                >
                  {!isEmptyColumn(column) && column.ctaBottom && (
                    <Button
                      href={column.ctaBottom.href}
                      variant='primaryFill'
                      size='sm'
                      onClick={() => {
                        column.ctaBottom!.onClick?.();
                        onCtaClick?.(column.id, column.ctaBottom!, 'bottom');
                        onProductClicked?.(column.id);
                      }}
                    >
                      {column.ctaBottom.label}
                    </Button>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Flex>
    </Flex>
  );
};

ComparisonTableDesktop.displayName = 'ComparisonTableDesktop';
